import { useState } from 'react';

/**
 * One row in the Operations action list. Each entry encapsulates an
 * admin operation that's confirm-required, observable, and idempotent.
 *
 * `run` is the entire side-effecting flow; for actions backed by an
 * async job (e.g. customer sync), the `run` implementation owns the
 * trigger+poll dance and resolves with the final summary.
 */
export interface OpsAction {
  id: string;
  title: string;
  /** One-line description shown under the title. */
  description: string;
  /** Optional confirm copy. When omitted the action runs immediately. */
  confirm?: string;
  /** Resolves with a `summary` string on success; reject with an Error on failure. */
  run: () => Promise<{ summary: string; warnings?: string[] }>;
  /** Optional sub-label rendered under the run button (e.g. "Last run: 2h ago"). */
  meta?: string;
}

export interface OpsActionListProps {
  actions: OpsAction[];
}

interface RowState {
  status: 'idle' | 'running' | 'success' | 'error';
  message?: string;
  warnings?: string[];
  finishedAt?: Date;
}

export function OpsActionList({ actions }: OpsActionListProps) {
  const [rows, setRows] = useState<Record<string, RowState>>({});

  const runAction = async (action: OpsAction) => {
    if (action.confirm && !window.confirm(action.confirm)) return;

    setRows((prev) => ({ ...prev, [action.id]: { status: 'running' } }));

    try {
      const result = await action.run();
      setRows((prev) => ({
        ...prev,
        [action.id]: {
          status: 'success',
          message: result.summary,
          warnings: result.warnings,
          finishedAt: new Date(),
        },
      }));
    } catch (err) {
      setRows((prev) => ({
        ...prev,
        [action.id]: {
          status: 'error',
          message: err instanceof Error ? err.message : String(err),
          finishedAt: new Date(),
        },
      }));
    }
  };

  return (
    <div className="bg-base-100 shadow rounded-lg divide-y divide-base-300">
      {actions.map((action) => {
        const state = rows[action.id] ?? { status: 'idle' as const };
        return (
          <div key={action.id} className="p-4 flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-base-content">{action.title}</h3>
              <p className="text-sm text-base-content/70 mt-0.5">{action.description}</p>
              {state.status !== 'idle' && (
                <div className="mt-2">
                  <StatusBadge state={state} />
                  {state.message && (
                    <p
                      className={`text-xs mt-1 ${
                        state.status === 'error' ? 'text-error' : 'text-base-content/70'
                      }`}
                    >
                      {state.message}
                    </p>
                  )}
                  {state.warnings && state.warnings.length > 0 && (
                    <ul className="text-xs text-warning mt-1 list-disc list-inside">
                      {state.warnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {action.meta && state.status === 'idle' && (
                <p className="text-xs text-base-content/50 mt-1">{action.meta}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => runAction(action)}
              disabled={state.status === 'running'}
              className="btn btn-sm btn-primary shrink-0"
            >
              {state.status === 'running' ? 'Running…' : 'Run'}
            </button>
          </div>
        );
      })}
    </div>
  );
}

function StatusBadge({ state }: { state: RowState }) {
  if (state.status === 'running') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        Running…
      </span>
    );
  }
  if (state.status === 'success') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Completed{state.finishedAt ? ` · ${state.finishedAt.toLocaleTimeString()}` : ''}
      </span>
    );
  }
  if (state.status === 'error') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        Failed{state.finishedAt ? ` · ${state.finishedAt.toLocaleTimeString()}` : ''}
      </span>
    );
  }
  return null;
}
